/**
 * 붙여넣기·드래그에서 **파일을 꺼낸다** (2026-09-10).
 *
 * 채팅 입력창과 AI 컴포저가 **같은 규칙**을 쓴다 — 한 곳에서 되는 것이 다른 곳에서 안 되면
 * 그것부터 결함으로 읽힌다.
 *
 * ## ★★★ 화면 붙여넣기(스크린샷)는 `files` 에 없을 수 있다
 *
 * 브라우저마다 다르다 — 대부분 `DataTransfer.files` 에 넣지만, 어떤 경로에서는 `items` 에만
 * `kind:'file'` 로 들어온다(클립보드 이미지가 특히 그렇다). **둘 다 봐야** 「어떤 날은 되고
 * 어떤 날은 안 되는」 모양을 피한다. 같은 파일이 양쪽에 다 있을 수 있으므로 합칠 때 겹치는
 * 것을 걸러 낸다.
 *
 * ## ★★ 붙여넣기는 **막지 않는다**
 *
 * 글과 그림이 함께 실린 붙여넣기(워드·한글에서 흔하다)에서 `preventDefault()` 를 부르면
 * **글이 안 들어간다.** 파일은 우리가 따로 올리고, 글은 브라우저가 하던 대로 넣게 둔다.
 * (드롭은 다르다 — 막지 않으면 브라우저가 그 파일로 **화면을 옮겨 버린다**.)
 */

/** 클립보드·드래그 꾸러미에서 파일을 모은다. 없으면 빈 배열. */
export function filesFromTransfer(dt: DataTransfer | null | undefined): File[] {
  if (!dt) return [];
  const out: File[] = [];
  const seen = new Set<string>();

  const push = (f: File | null): void => {
    if (!f) return;
    /* 이름·크기·수정시각이 같으면 같은 파일로 본다 — `files` 와 `items` 에 겹쳐 들어온 것. */
    const key = `${f.name} ${f.size} ${f.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(f);
  };

  for (const f of Array.from(dt.files ?? [])) push(f);
  for (const it of Array.from(dt.items ?? [])) {
    if (it.kind === 'file') push(it.getAsFile());
  }
  return out;
}

/**
 * 지금 끌고 오는 것이 **파일인가** — 글자를 끌어다 놓는 것과 가른다.
 * ★ `dragover` 시점에는 `files` 가 비어 있고 `items[].kind` 만 믿을 수 있다(브라우저 규약).
 */
export function transferHasFiles(dt: DataTransfer | null | undefined): boolean {
  if (!dt) return false;
  if (dt.items && dt.items.length > 0) {
    return Array.from(dt.items).some((i) => i.kind === 'file');
  }
  if (dt.types && Array.from(dt.types).includes('Files')) return true;
  return (dt.files?.length ?? 0) > 0;
}
